import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { gql } from 'graphql-request';
import { PracticeAnalyticsAggregations, PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { useAnalyticsOrgLocations, TabbedTable } from '@frontend/analytics-shared';
import { processExportableData } from '@frontend/file-download-helper';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { formatters, useIsSmallMode } from '@frontend/shared';
import { emptyStateGraphics, TableColumnConfig, useAlert } from '@frontend/design-system';
import { useShowBulkMessageButton } from '../../../hooks/use-show-bulk-message-button';
import { queryKeys } from '../../../query-keys';
import { trackingIds } from '../../../tracking-ids';
import { drillDownTableSize } from '../../../views/common-styles';
import { InlineStats } from '../../inline-stats';
import { BulkMessageButton } from '../bulk-message-button';
import { collectPatientIds, deriveExportValue } from '../helpers/patients';
import { getPracticeAnalyticsQueryVariables } from '../helpers/query-variables';
import { useSubViewTableColConfig } from '../helpers/subview-table-col-config';
import { usePatientsLastContacted } from '../hooks/use-patients-last-contacted';
import { usePracticeAnalyticsShallowStore } from '../hooks/use-practice-analytics-store';
import { NewPatientsMetric } from '../new-patients-metric';
import { SmallModePatientsList } from '../small-mode-patients-list';

const query = gql`
  query ($start: Int!, $end: Int!, $step: TimeStep!) {
    location {
      newPatients {
        details(start: $start, end: $end, step: $step) {
          patients {
            id
            productionAmount
            firstAppointmentDate
            firstAppointmentType
            nextAppointmentDate
            nextHygieneAppointment
            FirstName: firstName
            LastName: lastName
            PMID: pmid
            MobilePhone: mobilePhone
            HomePhone: homePhone
            WorkPhone: workPhone
            Email: email
            PreferredName: preferredName
            Birthdate: birthdate
            Gender: gender
          }
        }
        totals(start: $start, end: $end, step: $step) {
          patients
          production
          unscheduledProductionPerDay {
            date
            amount
          }
        }
      }
    }
  }
`;

type Tab = 'all' | 'hygiene' | 'restorative';

type CategorizedPatients = {
  all: PracticeAnalyticsTypes.PatientInfo[];
  hygiene: PracticeAnalyticsTypes.PatientInfo[];
  restorative: PracticeAnalyticsTypes.PatientInfo[];
};

export const NewPatientsSubView: FC<React.PropsWithChildren<PracticeAnalyticsTypes.MetricSubViewProps>> = ({
  exportEnabled,
  onPatientsLoad,
}) => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { isSmallMode } = useIsSmallMode();
  const { demoData, filters, isDemoAccount, showDemoChipAndBanner } = usePracticeAnalyticsShallowStore(
    'demoData',
    'filters',
    'isDemoAccount',
    'showDemoChipAndBanner'
  );
  const { locationNames } = useAnalyticsOrgLocations({ isDemoAccount, module: 'PA' });
  const isBulkMessagingEnabled = useShowBulkMessageButton();

  const [categorizedData, setCategorizedData] = useState<CategorizedPatients>({
    all: [],
    hygiene: [],
    restorative: [],
  });

  const { data, isLoading } = useScopedQuery({
    queryKey: queryKeys.practiceAnalyticsCharts(`newPatientsSubView-${isDemoAccount}-${JSON.stringify(filters)}`),
    queryFn: () =>
      isDemoAccount || !filters.locations?.length
        ? null
        : PracticeAnalyticsApi.getPracticeAnalyticsRecords<PracticeAnalyticsTypes.NewPatientsTrend>({
            locationIds: filters.locations,
            queries: [query],
            variables: getPracticeAnalyticsQueryVariables(filters),
          }),
    onError: () => {
      alert.error(t("Couldn't load the dashboard data. Please try again."));
    },
    select: (data) => (isDemoAccount ? demoData?.newPatients : data),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const processedData = useMemo(() => {
    return {
      aggregatedData: PracticeAnalyticsAggregations.newPatientsDetails(data),
      patientIds: Object.values(data?.data || {}).reduce((acc, locationDetails) => {
        return collectPatientIds(locationDetails?.location?.newPatients.details?.patients, acc);
      }, [] as string[]),
    };
  }, [data]);

  const { lastContactedDates, isLoading: isLoadingLastContacted } = usePatientsLastContacted({
    patientIds: processedData.patientIds,
  });

  useEffect(() => {
    const patients = processedData.aggregatedData.newPatients.details.patients || [];

    setCategorizedData(
      patients.reduce(
        (final, patient) => {
          let value = { ...final, all: [...final.all, patient] };
          if (patient.firstAppointmentType === 'hygiene') {
            value = { ...value, hygiene: [...value.hygiene, patient] };
          }
          if (patient.firstAppointmentType === 'restorative') {
            value = { ...value, restorative: [...value.restorative, patient] };
          }
          return value;
        },
        { all: [], hygiene: [], restorative: [] } as CategorizedPatients
      )
    );
    onPatientsLoad?.(patients);
  }, [onPatientsLoad, processedData]);

  const processExportableDataHandler = useCallback(
    (data: PracticeAnalyticsTypes.PatientInfo[]) =>
      // Translation is not needed as this is for export purpose
      processExportableData({
        columns: [
          'Patient Name',
          ...((filters.locations?.length || 0) > 1 ? ['Location Name'] : []),
          'Phone Number',
          'Email',
          'First Appt',
          'Last Contacted',
          '$ Value',
        ],
        data,
        deriveExportValue: (params) =>
          deriveExportValue({
            ...params,
            lastContactedDates,
            locationNames,
          }),
        sortColumn: 'productionAmount',
      }),
    [filters.locations, lastContactedDates, locationNames]
  );

  const tabs = useMemo(
    () => ({
      all: {
        data: categorizedData.all,
        label: t('All'),
        trackingId: trackingIds.practiceAnalytics.newPatientsAllTab,
      },
      restorative: {
        data: categorizedData.restorative,
        label: t('Restorative'),
        trackingId: trackingIds.practiceAnalytics.newPatientsRestorativeTab,
      },
      hygiene: {
        data: categorizedData.hygiene,
        label: t('Hygiene'),
        trackingId: trackingIds.practiceAnalytics.newPatientsHygieneTab,
      },
    }),
    [categorizedData.all, categorizedData.hygiene, categorizedData.restorative, t]
  );

  const smallModeTableProps: PracticeAnalyticsTypes.SmallModePatientsListProps<Tab> = {
    exportFileName: 'New Patients', // Translation not needed
    initialTab: 'all',
    isBulkMessagingEnabled,
    isDemoAccount,
    isExportEnabled: exportEnabled,
    isLoadingLastContacted,
    isLoadingPatients: isLoading,
    processExportableData: processExportableDataHandler,
    tabs,
  };

  const largeModeTableProps: PracticeAnalyticsTypes.LargeModeTableProps<Tab> = {
    ...smallModeTableProps,
    lastContactedDates,
    locationNames,
    multiLocations: (filters.locations?.length || 0) > 1,
  };

  return (
    <>
      <InlineStats
        data={[
          {
            key: 'newPatients',
            name: t('New Patients'),
            value: formatters.value(processedData.aggregatedData.newPatients.totals.patients || 0),
          },
          {
            key: 'completedProduction',
            name: t('Completed Production'),
            value: formatters.currency(processedData.aggregatedData.newPatients.totals.production || 0),
          },
          isSmallMode
            ? null
            : {
                key: 'uncapturedProduction',
                name: t('Uncaptured Production'),
                value: formatters.currency(
                  processedData.aggregatedData.newPatients.totals.unscheduledProductionPerDay.reduce(
                    (sum, { amount }) => sum + amount,
                    0
                  ) || 0
                ),
              },
        ]}
        isDemoAccount={showDemoChipAndBanner}
        isLoading={isLoading}
      />
      <NewPatientsMetric clickNoop isDrillDownPage />
      {isSmallMode ? (
        <SmallModePatientsList<Tab> {...smallModeTableProps} trackingIdBase='new-patients-small-mode' />
      ) : (
        <LargeModeTable {...largeModeTableProps} />
      )}
    </>
  );
};

const LargeModeTable = ({
  tabs,
  isBulkMessagingEnabled,
  isExportEnabled,
  isLoadingLastContacted,
  isLoadingPatients,
  processExportableData,
  ...rest
}: PracticeAnalyticsTypes.LargeModeTableProps<Tab>) => {
  const { t } = useTranslation('analytics');

  const middleColumns: TableColumnConfig<PracticeAnalyticsTypes.PatientInfo>[] = [
    {
      Header: t('First Appt'),
      headerLabel: t('First Appt'),
      accessor: ({ firstAppointmentDate }) => formatters.date(firstAppointmentDate),
      id: 'firstAppointmentDate',
      sortType: 'date',
      sortInverted: true,
      width: 200,
    },
  ];

  const tabsCommonConfig = {
    colConfig: useSubViewTableColConfig({
      actionsTrackingIdBase: 'new-patient',
      middleColumns,
      patientsLastContacted: {
        isLoading: isLoadingLastContacted,
        lastContactedDates: rest.lastContactedDates,
      },
    }),
    emptyStateConfig: 'users' as keyof typeof emptyStateGraphics,
    tableInstanceId: 'newPatientsPracticeSubView',
  };

  return (
    <TabbedTable<PracticeAnalyticsTypes.PatientInfo>
      {...rest}
      actions={BulkMessageButton}
      autorizedToExport={PracticeAnalyticsApi.auditPracticeAnalyticsExport}
      isExportReady={!isLoadingPatients && !isLoadingLastContacted}
      isLoading={isLoadingPatients}
      isSelectable={isBulkMessagingEnabled}
      processExportableData={processExportableData}
      showExportIcon={isExportEnabled}
      tabs={{
        all: {
          ...tabsCommonConfig,
          ...tabs.all,
        },
        restorative: {
          ...tabsCommonConfig,
          ...tabs.restorative,
        },
        hygiene: {
          ...tabsCommonConfig,
          ...tabs.hygiene,
        },
      }}
      trackingIdBase='new-patients'
      wrapperStyle={drillDownTableSize}
    />
  );
};
